import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useResource } from "@/lib/api";
import { formatTime } from "@/lib/format";

const TABLES = ["directors", "categories", "payment_methods", "financial_years", "transactions", "forecast_items",
  "director_entries", "director_adjustments", "misc_balances", "memo_notes", "users", "test_data", "import", "data_quality"];
const ACTIONS = ["create", "update", "delete", "import", "bulk_category", "settlement", "load", "remove", "seed", "set_current", "role_change"];

const Value = ({ v }) => {
  if (v == null) return <span className="text-muted-foreground">—</span>;
  const entries = Object.entries(v).filter(([k]) => k !== "id");
  return (
    <div className="font-mono text-xs space-y-0.5">
      {entries.map(([k, val]) => <div key={k}><span className="text-muted-foreground">{k}:</span> {String(val)}</div>)}
    </div>
  );
};

const FilterSelect = ({ value, onChange, items, placeholder, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-48" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{placeholder}</SelectItem>
      {items.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function AuditLogPage() {
  const [table, setTable] = useState("all");
  const [action, setAction] = useState("all");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearch(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);
  const params = new URLSearchParams();
  if (table !== "all") params.set("table", table);
  if (action !== "all") params.set("action", action);
  if (search) params.set("q", search);
  const { data } = useResource(`/audit-log?${params}`);

  return (
    <div className="rise">
      <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Audit log</h1>
      <p className="mt-3 text-muted-foreground text-base md:text-lg">Every change, who made it, and what it was before and after.</p>
      <div className="mt-10 flex flex-wrap gap-3">
        <FilterSelect value={table} onChange={setTable} items={TABLES} placeholder="All tables" testId="audit-log-entity-filter" />
        <FilterSelect value={action} onChange={setAction} items={ACTIONS} placeholder="All actions" testId="audit-log-action-filter" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search user or record…" className="w-64" data-testid="audit-log-search-input" />
      </div>
      <div className="mt-6 border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="audit-log-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              {["Time", "User", "Table", "Action", "Record", "Old value", "New value"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {(data || []).map((e) => (
              <tr key={e.id} className="border-b border-border last:border-0 align-top hover:bg-accent/40 transition-colors" data-testid="audit-log-row">
                <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{formatTime(e.time)}</td>
                <td className="px-4 py-3 whitespace-nowrap">{e.user_name}</td>
                <td className="px-4 py-3 whitespace-nowrap">{e.table.replace(/_/g, " ")}</td>
                <td className="px-4 py-3"><span className="font-mono text-xs uppercase text-emerald-300">{e.action}</span></td>
                <td className="px-4 py-3">
                  <div>{e.record_label || "—"}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{e.record}</div>
                </td>
                <td className="px-4 py-3"><Value v={e.old_value} /></td>
                <td className="px-4 py-3"><Value v={e.new_value} /></td>
              </tr>
            ))}
            {data && data.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No entries match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
